/**
 * Email preview dialog and the "behind each rate" tables (the lender, product and price behind
 * every emailed rate — for the MLO only; the borrower's email never names a lender).
 */
import { useEffect, useState } from 'react';
import { REALTOR_COLUMNS, REALTOR_ROWS, UWM_PROGRAMS, type ManaGridCell, type ManaOptionsSentRow } from '@mana/shared';
import { Button } from '../../ui/Button.js';
import { fmtMoney0, fmtPct3, fmtPrice, fmtWhen } from './shared.js';

const REALTOR_COLUMN_KEYS = new Set<string>(REALTOR_COLUMNS.map((c) => c.key));
/** Realtor cells carry the column key as programKey and the program label as kind. */
export const isRealtorCells = (cells: ManaGridCell[]) => cells.length > 0 && cells.every((c) => REALTOR_COLUMN_KEYS.has(c.programKey));

export function RealtorCellsTable({ cells }: { cells: ManaGridCell[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px] text-xs">
        <thead className="text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          <tr>
            <th className="py-1 pr-3">Loan program</th>
            {REALTOR_COLUMNS.map((c) => (
              <th key={c.key} className="py-1 pr-3">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {REALTOR_ROWS.map((row) => (
            <tr key={row.key} className="border-t border-slate-100 align-top">
              <td className="py-1.5 pr-3 font-medium text-slate-700">{row.label}</td>
              {REALTOR_COLUMNS.map((col) => {
                const c = cells.find((x) => x.kind === row.label && x.programKey === col.key);
                if (!c?.winner) {
                  return (
                    <td key={col.key} className="py-1.5 pr-3 text-amber-700">
                      Call for pricing
                    </td>
                  );
                }
                const w = c.winner;
                const others = c.candidates.filter((x) => !(x.source === w.source && x.product === w.product && x.rate === w.rate)).slice(0, 3);
                return (
                  <td key={col.key} className="py-1.5 pr-3">
                    <p className="font-semibold tabular-nums text-slate-800">
                      {fmtPct3(w.rate)}
                      {c.miFrom ? <span className="ml-1 text-[10px] font-normal text-brand-gold-800">{c.miFrom}</span> : null}
                    </p>
                    <p className="text-slate-600">
                      <span className="font-medium">{w.lender}</span> · {w.product}
                    </p>
                    <p className="tabular-nums text-slate-500">
                      price {fmtPrice(w.price)} · {w.credit >= 0 ? `credit ${fmtMoney0(w.credit)}` : `cost ${fmtMoney0(-w.credit)}`} · as of {fmtWhen(w.asOf)}
                    </p>
                    {others.length > 0 && <p className="mt-0.5 text-[10px] text-slate-400">next: {others.map((o) => `${o.lender} ${fmtPct3(o.rate)}`).join(' · ')}</p>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function GridCellsTable({ cells }: { cells: ManaGridCell[] }) {
  if (cells.length === 0) return null;
  const terms = [...new Set(cells.map((c) => c.termYears))].sort((a, b) => b - a);
  const programs = UWM_PROGRAMS.filter((p) => cells.some((c) => c.programKey === p.key));
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px] text-xs">
        <thead className="text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          <tr>
            <th className="py-1 pr-3">Term</th>
            {programs.map((p) => (
              <th key={p.key} className="py-1 pr-3">
                {p.emailLabel}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {terms.map((t) => (
            <tr key={t} className="border-t border-slate-100 align-top">
              <td className="py-1.5 pr-3 font-medium text-slate-700">{t} yr</td>
              {programs.map((p) => {
                const c = cells.find((x) => x.termYears === t && x.programKey === p.key);
                if (!c?.winner) {
                  return (
                    <td key={p.key} className="py-1.5 pr-3 text-amber-700">
                      Contact your loan officer
                    </td>
                  );
                }
                const w = c.winner;
                const others = c.candidates.filter((x) => x !== w && !(x.source === w.source && x.product === w.product && x.rate === w.rate)).slice(0, 3);
                return (
                  <td key={p.key} className="py-1.5 pr-3">
                    <p className="font-semibold tabular-nums text-slate-800">{fmtPct3(w.rate)}</p>
                    <p className="text-slate-600">
                      <span className="font-medium">{w.lender}</span> · {w.product}
                    </p>
                    <p className="tabular-nums text-slate-500">
                      price {fmtPrice(w.price)} · {w.credit >= 0 ? `credit ${fmtMoney0(w.credit)}` : `cost ${fmtMoney0(-w.credit)}`} · as of {fmtWhen(w.asOf)}
                    </p>
                    <p className="text-slate-400" title={c.miFrom ?? ''}>
                      MI {c.mi != null ? `${fmtMoney0(c.mi)}/mo` : '—'}
                      {c.miFrom ? ` · ${c.miFrom}` : ''}
                    </p>
                    {others.length > 0 && (
                      <p className="mt-0.5 text-[10px] text-slate-400">
                        next: {others.map((o) => `${o.lender} ${fmtPct3(o.rate)}`).join(' · ')}
                      </p>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function OptionsRowsTable({ rows }: { rows: ManaOptionsSentRow[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] text-xs">
        <thead className="text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          <tr>
            <th className="py-1 pr-3">Lender · Product</th>
            <th className="py-1 pr-3 text-right">Rate</th>
            <th className="py-1 pr-3 text-right">Price</th>
            <th className="py-1 pr-3 text-right">P&amp;I</th>
            <th className="py-1 pr-3 text-right">Credit</th>
            <th className="py-1">Rate as of</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              <td className="py-1 pr-3 text-slate-700">
                <span className="font-medium">{r.lender || 'Manual'}</span> · {r.product}
              </td>
              <td className="py-1 pr-3 text-right tabular-nums">{r.rate}</td>
              <td className="py-1 pr-3 text-right tabular-nums">{r.points}</td>
              <td className="py-1 pr-3 text-right tabular-nums">{r.monthlyPayment}</td>
              <td className="py-1 pr-3 text-right tabular-nums">{r.lenderCredit}</td>
              <td className="py-1 text-slate-500">{r.asOf ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface PreviewContent {
  title: string;
  subject: string | null;
  html: string | null;
  /** The email the old module sends today (moving a schedule), shown on a second tab. */
  currentHtml?: string | null;
  cells: ManaGridCell[];
  rows: ManaOptionsSentRow[];
  notes: string[];
  error?: string | null;
}

export function EmailPreviewDialog({ content, onClose, actions }: { content: PreviewContent; onClose: () => void; actions?: React.ReactNode }) {
  const [tab, setTab] = useState<'mana' | 'current'>('mana');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const html = tab === 'current' ? content.currentHtml : content.html;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={content.title} className="surface-card my-6 w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-brand-navy-800">{content.title}</h3>
            {content.subject && <p className="mt-0.5 truncate text-xs text-slate-500">Subject: {content.subject}</p>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {actions}
            <Button size="sm" variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
        {content.error && <p className="mx-5 mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{content.error}</p>}
        {content.notes.length > 0 && (
          <ul className="mx-5 mt-3 list-disc space-y-0.5 rounded-lg bg-amber-50 px-8 py-2 text-[11px] text-amber-800">
            {content.notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        )}
        {(content.cells.length > 0 || content.rows.length > 0) && (
          <div className="px-5 pt-3">
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Behind each rate · only you see this</p>
            {content.cells.length > 0 ? isRealtorCells(content.cells) ? <RealtorCellsTable cells={content.cells} /> : <GridCellsTable cells={content.cells} /> : <OptionsRowsTable rows={content.rows} />}
          </div>
        )}
        {html != null || content.currentHtml ? (
          <div className="px-5 pb-5 pt-3">
            {content.currentHtml && (
              <div className="mb-2 inline-flex rounded-lg border border-slate-200 bg-white p-0.5 text-xs">
                {(
                  [
                    ['mana', 'Mana Pricing email'],
                    ['current', "Today's email"],
                  ] as const
                ).map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setTab(k)} className={'rounded-md px-3 py-1 font-medium ' + (tab === k ? 'bg-brand-navy-700 text-white' : 'text-slate-600 hover:bg-slate-50')}>
                    {label}
                  </button>
                ))}
              </div>
            )}
            {html ? (
              <iframe title="Email preview" sandbox="" srcDoc={html} className="h-[70vh] w-full rounded-lg border border-slate-200 bg-white" />
            ) : (
              <p className="text-xs text-slate-500">No email to show.</p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
